import React, { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Grid, Box, Typography } from '@mui/material';
import map from 'lodash/map';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { Fields } from '@pv/common/components';
import { useEventTypes } from '../../hooks/useEventTypes';
import { useVenues } from '../../providers/venues';
import EventTimeEdit from './EventTimeEdit';
import SpaceSelector from '../../components/SpaceSelector';

const spaceAvailabilityQuery = gql`
  query SpaceAvailability($venueId: ID!, $dateTimeRange: DateTimeRangeInput!) {
    spaceAvailability(venueId: $venueId, dateTimeRange: $dateTimeRange)
  }
`;

const NewEventInfoArea = ({
  forceValidate,
  eventName,
  setEventName,
  groupSize,
  setGroupSize,
  venueId,
  setVenueId,
  spaceIds,
  setSpaceIds,
  startDate,
  setStartDate,
  startOffset,
  setStartOffset,
  endDate,
  setEndDate,
  endOffset,
  setEndOffset,
  budget,
  setBudget,
  eventTypeName,
  setEventTypeName,
  users,
  setUserId,
  userId,
}) => {
  const { venues } = useVenues();
  const { eventTypeOptions } = useEventTypes();
  const venuesVals = map(venues, (v) => [v.id, v.internalName || v.name]);
  const showVenuesSelector = venues?.length > 1;

  const skip = !venueId;

  const variables = {
    venueId,
    dateTimeRange: {
      startDate: startDate && moment(startDate).format('YYYY-MM-DD'),
      endDate: endDate && moment(endDate).format('YYYY-MM-DD'),
      startOffset: parseInt(startOffset),
      endOffset: parseInt(endOffset),
    },
  };

  const { data } = useQuery(spaceAvailabilityQuery, { variables, skip });
  const spaceAvailability = data?.spaceAvailability;
  const venue = venues?.find((v) => v.id === venueId);
  const multiDayEnabled =
    venue?.featureScope?.premiumFeaturesEnabled && venue?.multiDayEnabled;

  useEffect(() => {
    if (forceValidate && !eventName) {
      const variant = 'error';
      enqueueSnackbar('Event Name Required', {
        variant,
        SnackbarProps: {
          'data-cy': 'event-name-required-alert',
        },
      });
    }
  }, [forceValidate]);

  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        padding: '16px',
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Add Event Info</Typography>
        </Grid>
        <Grid item xs={12}>
          <Fields.PvTextField
            autoFocus
            required
            label="Event Name"
            name="eventName"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            forceValidate={forceValidate}
            data-cy="create-event-name-text-field"
          />
        </Grid>

        <Grid item xs={12}>
          <EventTimeEdit
            startDate={startDate}
            startOffset={startOffset}
            endDate={endDate}
            endOffset={endOffset}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            setStartOffset={setStartOffset}
            setEndOffset={setEndOffset}
            multiDayEnabled={multiDayEnabled}
            venue={venue}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Fields.PvIntegerField
            label="Group Size"
            name="groupSize"
            value={groupSize}
            onChange={(e) => setGroupSize(e.target.value)}
            data-cy="create-event-group-size-text-field"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Fields.PvCurrencyMaskField
            showCents={false}
            label="Budget"
            value={budget}
            name="budget"
            onChange={(e) => setBudget(e.target.value)}
            data-cy="create-event-budget-text-field"
          />
        </Grid>

        {showVenuesSelector && (
          <Grid item xs={12} sm={6}>
            <Fields.PvDropDown
              label="Venue"
              name="venue"
              vals={venuesVals}
              onChange={(e) => setVenueId(e.target.value)}
              value={venueId}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={showVenuesSelector ? 6 : 12}>
          <SpaceSelector
            spaceAvailability={spaceAvailability}
            venue={venue}
            onChange={(e) => setSpaceIds(e.target.value)}
            value={spaceIds}
          />
        </Grid>

        <Grid item xs={12}>
          {eventTypeOptions && (
            <Fields.PvDropDown
              variant="outlined"
              shrink={true}
              value={eventTypeName || ''}
              label="Event Type"
              onChange={(e) => setEventTypeName(e.target.value)}
              vals={[['', 'None'], ...eventTypeOptions.map((et) => [et, et])]}
              name="eventType"
            />
          )}
        </Grid>
        {venue?.featureScope?.premiumFeaturesEnabled && (
          <Grid item xs={12}>
            <Fields.PvDropDown
              shrink={true}
              label="Event Owner"
              name="eventOwner"
              vals={[['', 'None'], ...users.map((u) => [u.id, u.fullName])]}
              onChange={(e) => setUserId(e.target.value)}
              value={userId || ''}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default NewEventInfoArea;
